import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState, type FormEvent } from 'react'
import { dashboardQuery, devicesTableQuery, eventsTableQuery, type Row } from '../api/saveenergee'
import { today, type Period } from '../api/securitee'
import { Content } from '../ui/Shell'
import { DataTable, useTableState, type TableColumn } from '../ui/DataTable'
import { Spinner } from '../ui/Spinner'
import { durationString } from '../easeedesk/format'
import { PeriodFilter } from '../securitee/DevicesPage'
import { deviceColumns, eventColumns, type Column } from './columns'
import { SaveenergeeTabs, send } from './Tabs'
import { SettingsCheck } from '../ui/SettingsParts'

/**
 * Saveenergee - `all_devices` (#allDevices, devicesSummaryData) cu perioada
 * din antet (`SaveenergeeDevices.dataXhr`: azi), randul desfasurat cu cele
 * patru file si actiunile Hibernate / Shut down (`SaveenergeeDeviceActions`,
 * cu fereastra #shutdownDeviceModal); `events` (devicesEventsData); si
 * tabloul `getDashboard` cu cele cinci cifre ramase dupa scoaterea amCharts.
 */

function ListPage({ id, columns, query, defaultOrder, details, onAction, children }: {
  id: string
  columns(currency: string): Column[]
  query: typeof devicesTableQuery | typeof eventsTableQuery
  defaultOrder: number
  details?(row: Row, period: Period): React.ReactNode
  onAction?(kind: 'hibernate' | 'shutdown', deviceId: number): void
  children?: React.ReactNode
}) {
  const [period, setPeriod] = useState<Period>(() => ({ from: today(), to: today() }))
  const [state, setState] = useTableState(id, { start: 0, length: 25, order: { column: defaultOrder, dir: 'desc' }, search: '' })
  const cols = columns('')
  const orderColumn = cols.find((c) => c.index === state.order.column) ?? cols[defaultOrder]!
  const q = useQuery({
    ...query({ draw: 1, start: state.start, length: state.length, orderColumn: orderColumn.index, orderName: orderColumn.name ?? '', orderDir: state.order.dir, search: state.search, ...period }),
    placeholderData: keepPreviousData,
  })
  const currency = q.data?.currency ?? ''
  const onTableClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const a = (e.target as HTMLElement).closest('a.goHibernate, a.goShutdown')
    if (!a || !onAction) return
    e.preventDefault()
    onAction(a.classList.contains('goHibernate') ? 'hibernate' : 'shutdown', Number(a.getAttribute('data-id')))
  }
  return (
    <Content>
      <div id="content" onClick={onTableClick}>
        <DataTable
          id={id}
          columns={columns(currency) as TableColumn[]}
          rows={q.data?.data ?? []}
          total={q.data?.recordsTotal ?? 0}
          fetching={q.isFetching}
          error={q.isError ? (q.error as Error).message : null}
          onRetry={() => void q.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId)}
          headerButtons={<PeriodFilter period={period} onChange={setPeriod} />}
          details={details ? (row) => details(row, period) : undefined}
        />
        {children}
      </div>
    </Content>
  )
}

/** devices/modals/shutdownDevice.php: fortare, secunde, mesaj. */
export function ShutdownModal({ deviceId, onClose }: { deviceId: number; onClose(): void }) {
  return (
    <div className="modal fade in" id="shutdownDeviceModal" role="dialog" style={{ display: 'block', background: 'rgba(0,0,0,.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <form id="shutdownDeviceForm" onSubmit={(e: FormEvent<HTMLFormElement>) => {
            e.preventDefault()
            const d = new FormData(e.currentTarget)
            void send(`/saveenergee/devices/${deviceId}/shutdown`, {
              force_shutdown: d.get('force_shutdown') ? '1' : '0',
              shutdown_time: String(d.get('shutdown_time') ?? ''),
              shutdown_message: String(d.get('shutdown_message') ?? ''),
            }, 'Shutdown instruction was sent!', onClose)
          }}>
            <div className="modal-header">
              <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
              <h4 className="modal-title">Shut down device</h4>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Time until shutdown (seconds)</label>
                <input type="number" min={0} className="form-control" name="shutdown_time" defaultValue={30} />
              </div>
              <div className="form-group">
                <label>Message shown to the user</label>
                <input type="text" className="form-control" name="shutdown_message" defaultValue="" />
              </div>
              <div className="form-group">
                <SettingsCheck className="iCheckForce" name="force_shutdown" value="1" label="Force running applications to close" />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-default" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-danger goShutdownDevice">Shut down</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export function SaveenergeeDevicesPage() {
  const [shutdown, setShutdown] = useState<number | null>(null)
  return (
    <ListPage
      id="allDevices"
      columns={deviceColumns}
      query={devicesTableQuery}
      defaultOrder={2}
      details={(row, period) => <SaveenergeeTabs id={Number(row.ID)} period={period} />}
      onAction={(kind, id) => {
        if (kind === 'hibernate') void send(`/saveenergee/devices/${id}/hibernate`, {}, 'Hibernate instruction was sent')
        else setShutdown(id)
      }}
    >
      {shutdown !== null ? <ShutdownModal deviceId={shutdown} onClose={() => setShutdown(null)} /> : null}
    </ListPage>
  )
}

export function SaveenergeeEventsPage() {
  return <ListPage id="allEvents" columns={eventColumns} query={eventsTableQuery} defaultOrder={6} />
}

/** saveenergee/dashboard/dashboard.php - cele cinci cifre. */
export function SaveenergeeDashboardPage() {
  const q = useQuery(dashboardQuery())
  if (q.error) return <Content><div className="alert alert-danger" role="alert">Something went wrong: {q.error.message}</div></Content>
  if (!q.data) return <Content><Spinner /></Content>
  const d = q.data
  const tiles: [string, string, string, string][] = [
    ['green-haze', 'fa fa-money-bill', `${d.last30DaysSaved} ${d.currency}`, 'Saved in the last 30 days'],
    ['blue-madison', 'fa fa-money-bill', `${d.lastYearSaved} ${d.currency}`, 'Saved in the last year'],
    ['blue-mauve', 'fa fa-desktop', `${d.averageSavedDevice} ${d.currency}`, 'Average saved per device'],
    ['red-intense', 'fa fa-clock', durationString(d.averageRunningTime), 'Average running time per day'],
    ['blue-madison', 'fa fa-bolt', String(d.activeDevices), 'Devices saving energy'],
  ]
  return (
    <Content>
      <div id="content">
        <div className="row">
          {tiles.map(([color, icon, number, label]) => (
            <div className="col-md-3" key={label}>
              <div className={`dashboard-stat ${color}`}>
                <div className="visual"><i className={icon}></i></div>
                <div className="details"><div className="number">{number}</div><div className="desc">{label}</div></div>
                <Link className="more" to="/saveenergee/all_devices">View more <i className="fa fa-arrow-circle-right"></i></Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Content>
  )
}
